<template>
  <div>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
      :show="loading"
    >
      <div v-if="quizProgress && quizProgress.id">
        <!-- Quiz Progress Header -->
        <b-card class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0">
              Quiz Progress Overview
            </h4>
            <b-button
              variant="outline-danger"
              size="sm"
              :disabled="resetting"
              @click="showResetModal"
            >
              <b-spinner
                v-if="resetting"
                small
                class="mr-1"
              />
              <feather-icon
                v-else
                icon="RotateCcwIcon"
                size="16"
                class="mr-1"
              />
              Reset Progress
            </b-button>
          </div>

          <!-- Progress Stats -->
          <b-row class="text-center">
            <b-col md="3">
              <div class="progress-stat">
                <h2 class="text-success mb-1">
                  {{ quizProgress.final_score }}%
                </h2>
                <p class="text-muted mb-0">
                  Final Score
                </p>
              </div>
            </b-col>
            <b-col md="3">
              <div class="progress-stat">
                <h2 class="text-primary mb-1">
                  {{ quizProgress.attempts }}
                </h2>
                <p class="text-muted mb-0">
                  Total Attempts
                </p>
              </div>
            </b-col>
            <b-col md="3">
              <div class="progress-stat">
                <h2 class="text-info mb-1">
                  {{ quizProgress.num_of_questions }}
                </h2>
                <p class="text-muted mb-0">
                  Questions
                </p>
              </div>
            </b-col>
            <b-col md="3">
              <div class="progress-stat">
                <h2 class="text-warning mb-1">
                  {{ correctAnswers }}/{{ quizProgress.num_of_questions }}
                </h2>
                <p class="text-muted mb-0">
                  Correct Answers
                </p>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- Detailed Answers -->
        <b-card>
          <h5 class="mb-3">
            Answer Details
          </h5>
          <div class="answers-grid">
            <div
              v-for="(answer, index) in quizProgress.metadata"
              :key="answer.question_id"
              class="answer-card mb-3 p-3 border rounded"
              :class="{
                'border-success bg-success-light': answer.is_correct,
                'border-danger bg-danger-light': !answer.is_correct
              }"
            >
              <div class="d-flex align-items-start justify-content-between">
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center mb-2">
                    <b-badge
                      :variant="answer.is_correct ? 'success' : 'danger'"
                      class="mr-2"
                    >
                      Q{{ index + 1 }}
                    </b-badge>
                    <h6 class="mb-0">
                      Question {{ index + 1 }}
                    </h6>
                  </div>
                  <div class="question-details">
                    <p class="text-muted small mb-1">
                      <strong>Question ID:</strong> {{ answer.question_id }}
                    </p>
                    <p class="text-muted small mb-1">
                      <strong>Answer ID:</strong> {{ answer.answer_id }}
                    </p>
                    <p class="text-muted small mb-0">
                      <strong>Answered at:</strong> {{ formatDate(answer.answered_at) }}
                    </p>
                  </div>
                </div>
                <div class="answer-status">
                  <b-badge
                    :variant="answer.is_correct ? 'success' : 'danger'"
                    pill
                    class="p-2"
                  >
                    <feather-icon
                      :icon="answer.is_correct ? 'CheckIcon' : 'XIcon'"
                      size="16"
                    />
                    {{ answer.is_correct ? 'Correct' : 'Incorrect' }}
                  </b-badge>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!-- No Progress State -->
      <div
        v-else-if="!loading"
        class="text-center py-5"
      >
        <feather-icon
          icon="HelpCircleIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h5 class="text-muted">
          No Quiz Progress Found
        </h5>
        <p class="text-muted">
          This driver hasn't started the learning center quiz yet.
        </p>
      </div>
    </b-overlay>

    <!-- Reset Confirmation Modal -->
    <b-modal
      id="reset-progress-modal"
      title="Reset Quiz Progress"
      title-class="text-danger"
      hide-footer
      centered
    >
      <div class="text-center">
        <feather-icon
          icon="AlertTriangleIcon"
          size="48"
          class="text-danger mb-3"
        />
        <h5 class="mb-3">
          Are you sure?
        </h5>
        <p class="mb-4">
          This will permanently reset all quiz progress for this driver.
          This action cannot be undone.
        </p>
      </div>

      <div class="d-flex justify-content-center">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          @click="$bvModal.hide('reset-progress-modal')"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="resetting"
          @click="resetProgress"
        >
          <b-spinner
            v-if="resetting"
            small
            class="mr-1"
          />
          Reset Progress
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BOverlay,
  BRow,
  BCol,
  BButton,
  BBadge,
  BSpinner,
  BModal,
} from 'bootstrap-vue'
import { ref, computed } from '@vue/composition-api'
import axiosIns from '@/libs/axios'
import { successNotification, errorNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BCard,
    BOverlay,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    BModal,
  },
  props: {
    driverData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, ctx) {
    const loading = ref(false)
    const resetting = ref(false)
    const quizProgress = ref(null)

    const correctAnswers = computed(() => {
      if (!quizProgress.value?.metadata) return 0
      return quizProgress.value.metadata.filter(answer => answer.is_correct).length
    })

    const fetchQuizProgress = async () => {
      if (!props.driverData?.id) return

      try {
        loading.value = true
        const response = await axiosIns.get(`/drivers/quiz/progress/${props.driverData.id}/`)
        const progressData = response.data.data.data

        // Check if progress data is empty object or null
        if (!progressData || Object.keys(progressData).length === 0) {
          quizProgress.value = null
        } else {
          quizProgress.value = progressData
        }
      } catch (error) {
        if (error.response?.status === 404) {
          quizProgress.value = null
        } else {
          errorNotification('Failed to load quiz progress')
        }
      } finally {
        loading.value = false
      }
    }

    const showResetModal = () => {
      ctx.root.$bvModal.show('reset-progress-modal')
    }

    const resetProgress = async () => {
      if (!props.driverData?.id) return

      try {
        resetting.value = true
        await axiosIns.post(`/drivers/quiz/progress/${props.driverData.id}/reset/`)
        successNotification('Quiz progress reset successfully')
        ctx.root.$bvModal.hide('reset-progress-modal')

        // Reload the data after reset
        await fetchQuizProgress()
      } catch (error) {
        const errorMsg = error?.response?.data?.message || 'Failed to reset quiz progress'
        errorNotification(errorMsg)
      } finally {
        resetting.value = false
      }
    }

    const formatDate = dateString => {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    // Fetch data on component mount
    fetchQuizProgress()

    return {
      loading,
      resetting,
      quizProgress,
      correctAnswers,
      showResetModal,
      resetProgress,
      formatDate,
    }
  },
}
</script>

<style scoped>
.progress-stat {
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: #f8f9fa;
  margin-bottom: 1rem;
}

.answer-card {
  transition: all 0.2s ease;
}

.answer-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.bg-success-light {
  background-color: rgba(40, 167, 69, 0.1) !important;
}

.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.answers-grid {
  display: grid;
  gap: 1rem;
}

@media (min-width: 768px) {
  .answers-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.question-details {
  font-size: 0.875rem;
}

.answer-status {
  min-width: 120px;
  text-align: center;
}
</style>
